import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle, WarningOctagon, CaretRight, CaretDown } from "@phosphor-icons/react";
import { fmtMoney } from "../lib/format";
import { reconciliationFailed } from "../lib/filingReadiness";

/**
 * v0.4.17 UI2 — Actionable reconciliation banner.
 *
 * On SAFE_TO_FILE: minimal header, NO descriptions block (per spec
 * Q-rules-4 = a — a clean filing screen should be clean).
 *
 * On DO_NOT_FILE: group recon.deltas by rule prefix (R1/R3/R4) and
 * render each as an expand-in-place block carrying its i18n
 * description + resolution hint and a click-to-expand row list.
 * A synthetic REVIEW_QUEUE pseudo-rule accounts for the portion of
 * `rule_checks_failed` that doesn't surface as a delta (e.g. txns
 * with `needs_review=True` counted as failures in build_report).
 */

const RULE_ORDER = ["R1", "R2", "R3", "R4"];

function parseRuleId(label) {
  if (!label) return "UNKNOWN";
  const m = label.match(/^(R\d+)\b/);
  return m ? m[1] : "UNKNOWN";
}

function groupDeltas(deltas) {
  const groups = {};
  for (const d of deltas || []) {
    const id = parseRuleId(d.label);
    if (!groups[id]) groups[id] = [];
    groups[id].push(d);
  }
  return groups;
}

export const ReconciliationBanner = ({ recon }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState({});
  // v0.4.22 F1 — the full failure list collapses behind a "Show
  // details" toggle. The banner default state is the single-line
  // summary; the operator opts in to the per-rule blocks.
  const [detailsOpen, setDetailsOpen] = useState(false);

  const groups = useMemo(() => groupDeltas(recon?.deltas), [recon?.deltas]);
  // v0.5.7 REGRESSION FIX (READY/Cannot-file contradiction) — the
  // reconciliation banner reflects the R1-R4 calc facts ONLY, derived from
  // `recon.deltas` (the same signal computeFilingReadiness.reconciliation_ok
  // reads). It must NOT read `recon.do_not_file` / `rule_checks_failed`:
  // that stored calc-time snapshot folds in the mutable, live review-queue
  // count, so once reviews were acknowledged it still rendered a STALE red
  // "Cannot file — N check(s) failed" directly contradicting the green
  // readiness strip (and mis-counting: N rows vs a single review-queue
  // line). Review status is a separate LIVE gate (FilingReadinessStrip +
  // the review queue), never re-derived from this snapshot. Both the fail
  // state and the header count now come from ONE structure (`groups`), so
  // the count and the listed failures can never diverge.
  const failedCount = Object.keys(groups).length;
  const fail = reconciliationFailed(recon);

  if (!recon) return null;

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div
      data-testid="reconciliation-banner"
      data-status={fail ? "DO_NOT_FILE" : "SAFE_TO_FILE"}
      className={
        fail
          ? "bg-amber-50 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-600/50 text-amber-900 dark:text-amber-100"
          : "bg-emerald-600/10 dark:bg-emerald-900/40 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
      }
    >
      <div className="px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {fail
            ? <WarningOctagon size={16} weight="bold" className="text-amber-700 dark:text-amber-300 shrink-0" />
            : <CheckCircle size={16} weight="bold" className="shrink-0" />}
          <div className="text-xs truncate">
            {fail
              ? <span data-testid="reconciliation-banner-status">
                  {t("ui.reconciliation.do_not_file_compact", { count: failedCount })}
                </span>
              : <span data-testid="reconciliation-banner-status">
                  {t("ui.reconciliation.safe_to_file")}
                </span>}
          </div>
        </div>
        {fail && (
          <button
            type="button"
            data-testid="reconciliation-banner-details-toggle"
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="tabular-nums text-[10px] tracking-normal text-amber-700 dark:text-amber-300 hover:text-amber-100 flex items-center gap-1 shrink-0"
          >
            {detailsOpen
              ? <><CaretDown size={11} weight="bold" /> {t("ui.reconciliation.hide_details")}</>
              : <><CaretRight size={11} weight="bold" /> {t("ui.reconciliation.show_details")}</>}
          </button>
        )}
      </div>

      {/* F1 — full rule blocks render only on opt-in. Compact bar
          stays the persistent header above. */}
      {fail && detailsOpen && (
        <div data-testid="recon-rule-blocks" className="border-t border-amber-600/30 bg-amber-50 dark:bg-amber-900/40">
          <div className="px-4 py-2 tabular-nums text-[10px] tracking-normal opacity-70">
            {t("ui.reconciliation.rule_header")}
          </div>
          <ul className="divide-y divide-amber-600/20">
            {RULE_ORDER.map((id) => {
              const items = groups[id] || [];
              if (items.length === 0) return null;
              const open = !!expanded[id];
              return (
                <li key={id} data-testid={`recon-rule-${id}`}>
                  <button
                    type="button"
                    data-testid={`recon-rule-toggle-${id}`}
                    onClick={() => toggle(id)}
                    className="w-full text-left px-4 py-2 flex items-start gap-2 hover:bg-amber-900/20"
                  >
                    {open ? <CaretDown size={14} weight="bold" className="mt-1" /> : <CaretRight size={14} weight="bold" className="mt-1" />}
                    <div className="flex-1">
                      <div className="tabular-nums text-xs flex items-center gap-3">
                        <span className="font-bold">{t(`ui.reconciliation.rules.${id}.name`)}</span>
                        <span data-testid={`recon-rule-count-${id}`} className="bg-amber-900/40 border border-amber-600/40 px-2 py-0.5 rounded-full text-[10px]">
                          {items.length}
                        </span>
                      </div>
                      <div className="text-xs opacity-90 mt-1" data-testid={`recon-rule-description-${id}`}>
                        {t(`ui.reconciliation.rules.${id}.description`)}
                      </div>
                      <div className="text-xs opacity-80 mt-0.5 italic" data-testid={`recon-rule-resolution-${id}`}>
                        → {t(`ui.reconciliation.rules.${id}.resolution`)}
                      </div>
                    </div>
                    <span className="text-[10px] opacity-70 underline decoration-dotted mt-1">
                      {open ? t("ui.reconciliation.click_to_collapse") : t("ui.reconciliation.click_to_expand")}
                    </span>
                  </button>
                  {open && (
                    <div data-testid={`recon-rule-rows-${id}`} className="px-8 pb-2 tabular-nums text-[11px] space-y-0.5">
                      {items.slice(0, 100).map((d, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 opacity-90">
                          <span className="col-span-6 truncate">{d.label}</span>
                          <span className="col-span-2 text-right">{t("ui.reconciliation.exp")} {fmtMoney(d.expected)}</span>
                          <span className="col-span-2 text-right">{t("ui.reconciliation.act")} {fmtMoney(d.actual)}</span>
                          <span className="col-span-2 text-right font-bold">{t("ui.reconciliation.delta")} {fmtMoney(d.delta)}</span>
                        </div>
                      ))}
                      {items.length > 100 && (
                        <div className="opacity-70 italic">{t("ui.reconciliation.more_truncated", { n: items.length - 100, defaultValue: `+${items.length - 100} more (truncated)` })}</div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
